import { HeroCardShape, HeroCardButtonShape } from '../store/schema/HeroCardShape';
import { trace } from 'owa-trace';

const HERO_CARD_CONTENT_TYPE = 'application/vnd.microsoft.card.hero';
const THUMBNAIL_CONTENT_TYPE = 'application/vnd.microsoft.card.thumbnail';
const OPEN_URL_TYPE = 'openUrl';
const NONE_TITLE = 'None'; // TODO: remove this when it is no longer necessary (server issue logged on Dorado team)

export default function parseAttachmentJson(jsonPayload: any): HeroCardShape {
    if (jsonPayload.contentType === HERO_CARD_CONTENT_TYPE) {
        return parseHeroCard(jsonPayload);
    } else if (jsonPayload.contentType === THUMBNAIL_CONTENT_TYPE) {
        return parseThumbnail(jsonPayload);
    }

    return undefined;
}

function parseHeroCard(jsonPayload: any): HeroCardShape {
    try {
        let url: string;
        if (jsonPayload.content.tap && jsonPayload.content.tap.type === OPEN_URL_TYPE) {
            url = jsonPayload.content.tap.value;
        }

        return {
            title: stripHtml(getPropertyNormalized(jsonPayload.content, 'title')).text,
            text: stripHtml(getPropertyNormalized(jsonPayload.content, 'text')).text,
            images: parseImagesProperty(jsonPayload.content),
            buttons: parseButtonsProperty(jsonPayload.content),
            tapAction: parseTapProperty(jsonPayload.content),
            url: url,
        };
    } catch (error) {
        trace.info('Error parsing hero card: ' + error.message);
    }

    return undefined;
}

// Take the html string and safely extract the text from it. In some cases, if the string starts with an anchor tag then also extract the href from it and return that too
function stripHtml(html: string): { text: string; href?: string } {
    if (!html || html === NONE_TITLE) {
        return { text: '' };
    }

    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let href: string;
        if (doc.body) {
            const firstElement = doc.body.firstElementChild;
            if (firstElement && firstElement.tagName == 'A') {
                href = (<HTMLAnchorElement>firstElement).href;
            }
        }
        return { text: doc.body.textContent || '', href: href };
    } catch {
        return { text: '' };
    }
}

function parseThumbnail(jsonPayload: any): HeroCardShape {
    try {
        const cleanedTitle = stripHtml(getPropertyNormalized(jsonPayload.content, 'title'));
        return {
            title: cleanedTitle.text,
            text: stripHtml(getPropertyNormalized(jsonPayload.content, 'text')).text,
            images: parseImagesProperty(jsonPayload.content),
            url: cleanedTitle.href,
        };
    } catch (error) {
        trace.info('Error parsing thumbnail: ' + error.message);
    }

    return undefined;
}

function parseImagesProperty(jsonPayload: any): string[] {
    if (jsonPayload) {
        const images = getPropertyNormalized(jsonPayload, 'images');
        if (images) {
            return images.map(image => getPropertyNormalized(image, 'url'));
        }
    }

    return undefined;
}

function parseButtonsProperty(jsonPayload: any): HeroCardButtonShape[] {
    if (jsonPayload) {
        const buttons = getPropertyNormalized(jsonPayload, 'buttons');
        if (buttons) {
            return buttons.map(button => parseHeroCardButton(button));
        }
    }

    return undefined;
}

function parseTapProperty(jsonPayload: any): HeroCardButtonShape {
    if (jsonPayload) {
        const tapButton = getPropertyNormalized(jsonPayload, 'tap');
        if (tapButton) {
            return parseHeroCardButton(tapButton);
        }
    }

    return undefined;
}

// This is a temporary workaround because some providers return hero cards json with capitalized property names.
// Once the server-side change is made to return more standard json this will no longer be needed.
function getPropertyNormalized(jsonPayload: any, propertyName: string): any {
    if (jsonPayload[propertyName]) {
        return jsonPayload[propertyName];
    }

    propertyName = propertyName.charAt(0).toUpperCase() + propertyName.slice(1);

    if (jsonPayload[propertyName]) {
        return jsonPayload[propertyName];
    }

    return undefined;
}

function parseHeroCardButton(buttonJsonPayload: any): HeroCardButtonShape {
    return {
        title: getPropertyNormalized(buttonJsonPayload, 'title'),
        type: getPropertyNormalized(buttonJsonPayload, 'type'),
        value: getPropertyNormalized(buttonJsonPayload, 'value'),
    };
}
